import React, { useState, useEffect, useMemo } from 'react';
import confetti from 'canvas-confetti';

// --- CONSTANTS ---
const DEFAULT_LABELS = ['Never / No', 'Rarely', 'Sometimes', 'Often', 'Always / Yes'];
const AGREE_LABELS = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];

// --- DATA STORE ---
const ASSESSMENT_DATA = {
    a: {
        name: 'Eat & Hydrate',
        icon: '🍎',
        questions: [
            { text: 'I make sure to include quality protein with most of my meals.' },
            { text: 'I stay well hydrated, adding minerals or electrolytes when I need them.' },
            { text: 'My energy and appetite feel stable and steady between meals.' },
            { text: 'My digestion feels comfortable and my bowel movements are regular and well-formed.' },
            { text: 'The vast majority (over 80%) of what I eat is whole, minimally processed food.' },
            { text: 'I eat four or more different colours of plants each day.' }
        ]
    },
    b: {
        name: 'Sleep & De-Stress',
        icon: '😴',
        questions: [
            { text: 'I typically sleep for 7 to 8 hours and wake up feeling refreshed.', labels: ['Never', 'Rarely', 'Sometimes', 'Most Nights', 'Every Night'] },
            { text: 'I fall asleep within about 20 minutes and generally stay asleep through the night.' },
            { text: 'My bedtime and wake-up time are consistent, staying within a 30-60 minute window all week.' },
            { text: 'I follow a relaxing bedtime routine in a cool, dark, and quiet bedroom.' },
            { text: 'I avoid screens for at least 60-90 minutes before bed.' },
            { text: 'I avoid caffeine and other stimulants in the afternoon and evening.' }
        ]
    },
    c: {
        name: 'Move & Improve',
        icon: '💪',
        questions: [
            { text: 'I average between 7,000 and 10,000 steps on most days.' },
            { text: 'I do strength training at least twice a week.' },
            { text: 'I do cardio or conditioning 1 to 3 times a week at a suitable intensity.' },
            { text: 'I include 10-15 minutes of gentle recovery work (like stretching or mobility) most days.' },
            { text: 'After exercise, I feel recovered within a day or two, with no excessive soreness.' },
            { text: 'When I\'m sitting for long periods, I get up and move around about once an hour.' }
        ]
    },
    d: {
        name: 'Love, Purpose & Community',
        icon: '❤️',
        questions: [
            { text: 'I have at least two or three people I can truly rely on for support.', labels: ['None', 'Maybe one', 'Yes, two', 'Yes, three', 'Four or more'] },
            { text: "I'm part of a community that supports my goals and keeps me accountable.", labels: AGREE_LABELS },
            { text: 'My home and work life feel supportive, aligned with my values, and generally low-stress.', labels: ['Not at all', 'Rarely', 'Somewhat', 'Mostly', 'Completely'] },
            { text: 'I experience regular affectionate human touch and/or intimacy.' },
            { text: 'I feel cheerful most days and make time for fun, gratitude, or creativity.', labels: AGREE_LABELS },
            { text: 'I rarely feel lonely or isolated.', labels: AGREE_LABELS }
        ]
    }
};

const PILLAR_ORDER = ['a', 'b', 'c', 'd'];

// --- HELPERS ---
const getBand = (pcnt) => {
    if (pcnt <= 49) return { label: 'Needs attention first', class: 'text-red-500', stroke: 'stroke-red-500', fill: 'from-red-500 to-red-600' };
    if (pcnt <= 79) return { label: 'Improve next', class: 'text-amber-500', stroke: 'stroke-amber-500', fill: 'from-amber-500 to-amber-600' };
    return { label: 'Maintain & refine', class: 'text-green-500', stroke: 'stroke-green-500', fill: 'from-green-500 to-green-600' };
};

const encode = (data) => {
    return Object.keys(data).map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key])).join("&");
};

const useAnimatedCounter = (endValue, duration = 1500) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        let startTime = null;
        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * endValue));
            if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [endValue, duration]);
    return count;
};

// --- UI COMPONENTS ---
const Header = () => (
    <header className="sticky top-0 z-50 bg-[#1C2B41] border-b border-white/10 shadow-lg transition-all print:hidden">
        <div className="container mx-auto px-4 py-4 flex justify-center">
            <img src="https://philipwheelertherapy.com/wp-content/uploads/2025/09/Elevate40Logo.png" className="h-12 md:h-16" alt="Elevate40+ Logo" />
        </div>
    </header>
);

const WelcomeScreen = ({ onStart }) => (
    <div className="text-center px-4 py-8 md:py-16 flex flex-col items-center animate-fade-in print:hidden">
        <div className="relative w-32 h-32 mb-8 flex items-center justify-center">
            <div className="absolute inset-0 bg-[#B5D221]/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-2 border-2 border-[#B5D221] rounded-full animate-pulse delay-500"></div>
            <span className="text-5xl animate-bounce">🌟</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-white to-[#B5D221] text-transparent bg-clip-text mb-4">Discover Your Health Score</h1>
        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">Complete this comprehensive 4-pillar assessment to get your personalised health insights.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full max-w-5xl mx-auto mb-12">
            {PILLAR_ORDER.map(key => (
                <div key={key} className="bg-[#1C2B41] p-4 rounded-xl border border-white/10 text-center transition-transform hover:-translate-y-1">
                    <div className="text-4xl mb-3">{ASSESSMENT_DATA[key].icon}</div>
                    <h3 className="font-semibold text-white">{ASSESSMENT_DATA[key].name}</h3>
                </div>
            ))}
        </div>
        <button onClick={onStart} className="bg-[#B5D221] text-[#0F3E6D] font-bold text-2xl px-12 py-5 rounded-2xl shadow-2xl hover:scale-105 transition-transform active:scale-95">
            Start Your Assessment →
        </button>
    </div>
);

const MembershipStatusScreen = ({ onSelect, onBack }) => (
    <div className="max-w-md mx-auto px-4 py-16 text-center animate-fade-in print:hidden">
        <h2 className="text-4xl font-bold text-white mb-8">Are you a current Elevate 40+ Member?</h2>
        <div className="space-y-4">
            <button onClick={() => onSelect('non-member')} className="w-full bg-gray-700/60 text-white font-bold py-4 rounded-xl border border-white/10 hover:bg-gray-700">No, I am not a member</button>
            <button onClick={() => onSelect('member')} className="w-full bg-[#B5D221] text-[#0F3E6D] font-bold py-4 rounded-xl shadow-lg hover:bg-[#a2b91d]">Yes, I am a member</button>
        </div>
        <button onClick={onBack} className="mt-8 text-gray-400 hover:text-white">← Back</button>
    </div>
);

const UserDetailsForm = ({ type, onSubmit, onBack }) => {
    const isMember = type === 'member';
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', ageBracket: '' });
    const [consent, setConsent] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.email && formData.ageBracket && consent) {
            onSubmit(formData);
        }
    };

    return (
        <div className="max-w-md mx-auto py-12 px-4 animate-fade-in print:hidden">
            <form onSubmit={handleSubmit} className="bg-[#1C2B41] p-8 rounded-2xl border border-white/10 space-y-6 shadow-2xl text-left">
                <h2 className="text-2xl font-bold text-white mb-2">{isMember ? 'Welcome Back!' : 'Get Your Results'}</h2>
                {!isMember && (
                    <div className="grid grid-cols-2 gap-4">
                        <input type="text" placeholder="First Name" required className="w-full bg-white/5 border border-white/20 p-3 rounded-lg text-white outline-none focus:border-[#B5D221]" onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                        <input type="text" placeholder="Last Name" required className="w-full bg-white/5 border border-white/20 p-3 rounded-lg text-white outline-none focus:border-[#B5D221]" onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
                    </div>
                )}
                <input type="email" placeholder="Email Address" required className="w-full bg-white/5 border border-white/20 p-3 rounded-lg text-white outline-none focus:border-[#B5D221]" onChange={e => setFormData({ ...formData, email: e.target.value })} />
                <div className="flex gap-4">
                    {['Under 40', '40 or Over'].map(b => (
                        <button key={b} type="button" onClick={() => setFormData({ ...formData, ageBracket: b })} className={`flex-1 p-3 rounded-lg border-2 transition-all ${formData.ageBracket === b ? 'border-[#B5D221] bg-[#B5D221]/10' : 'border-white/10 text-gray-400'}`}>{b}</button>
                    ))}
                </div>
                <label className="flex items-start space-x-3 text-sm text-gray-400 cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={consent} onChange={e => setConsent(e.target.checked)} />
                    <span>I agree to receive my results via email.</span>
                </label>
                <button type="submit" disabled={!consent || !formData.ageBracket} className="w-full bg-[#B5D221] text-[#0F3E6D] font-bold py-4 rounded-lg">Continue</button>
                <button type="button" onClick={onBack} className="w-full text-center text-gray-400 text-sm">← Go Back</button>
            </form>
        </div>
    );
};

const AssessmentScreen = ({ pillarKey, questionIndex, answers, onAnswer, onPrev, isVisible, tempSelection }) => {
    const pillar = ASSESSMENT_DATA[pillarKey];
    // Handle new object structure or fallback string
    const questionObj = pillar.questions[questionIndex];
    const questionText = typeof questionObj === 'string' ? questionObj : questionObj.text;
    const currentLabels = questionObj.labels || DEFAULT_LABELS;

    const currentAnswer = answers[`${pillarKey}-${questionIndex}`];
    const currentQuestionNumber = PILLAR_ORDER.indexOf(pillarKey) * 6 + questionIndex + 1;
    const progress = (currentQuestionNumber / 24) * 100;

    return (
        <div className="max-w-2xl mx-auto py-8 px-4 print:hidden">
            <div className="mb-8">
                <div className="flex justify-between items-end mb-2">
                    <span className="font-bold text-white uppercase tracking-widest">{pillar.icon} {pillar.name}</span>
                    <span className="text-gray-400 text-sm">{currentQuestionNumber} / 24</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#B5D221] transition-all duration-700 ease-in-out" style={{ width: `${progress}%` }}></div>
                </div>
            </div>

            <div className={`bg-[#1C2B41] p-8 rounded-2xl border border-white/10 shadow-2xl min-h-[300px] flex flex-col justify-between transition-all duration-300 ease-in-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div>
                    <h3 className="text-2xl text-white font-medium mb-10 leading-relaxed">{questionText}</h3>
                    <div className="space-y-3 mb-8">
                        {currentLabels.map((label, i) => {
                            const isSelected = currentAnswer === i || tempSelection === i;
                            return (
                                <button key={i} onClick={() => onAnswer(i)} className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-100 active:scale-[0.98] ${isSelected ? 'border-[#B5D221] bg-[#B5D221]/10 text-[#B5D221]' : 'border-white/10 text-gray-300 hover:border-white/40 hover:bg-white/5'}`}>
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="flex justify-start">
                    <button onClick={onPrev} className="text-gray-400 hover:text-white px-4 py-2 border border-white/10 rounded-lg transition-colors">← Back</button>
                </div>
            </div>
        </div>
    );
};

const ResultsScreen = ({ answers, membershipStatus, adminData }) => {
    const scores = useMemo(() => {
        const pillarScores = {}; let total = 0;
        PILLAR_ORDER.forEach(k => {
            let sum = 0;
            ASSESSMENT_DATA[k].questions.forEach((_, i) => sum += (answers[`${k}-${i}`] || 0));
            pillarScores[k] = Math.round((sum / 24) * 100);
            total += pillarScores[k];
        });
        return { pillarScores, overall: Math.round(total / 4) };
    }, [answers]);

    const overallCount = useAnimatedCounter(scores.overall);
    const band = getBand(scores.overall);

    return (
        <div className="max-w-4xl mx-auto py-12 px-4 text-center animate-fade-in">
            {/* SCREEN VIEW */}
            <div className="print:hidden">
                <div className="mb-12 flex flex-col md:flex-row items-center justify-center gap-12">
                    <div className="relative w-48 h-48 md:w-56 md:h-56">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                            <path className="text-white/10" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                            <path className={band.stroke} strokeWidth="3" strokeDasharray={`${overallCount}, 100`} strokeLinecap="round" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" style={{ transition: 'stroke-dasharray 1.5s ease-out' }} />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-5xl font-black text-white">{overallCount}%</span>
                            <span className="text-xs text-gray-400 uppercase tracking-widest text-white/60">Overall Score</span>
                        </div>
                    </div>
                    <div className="text-left">
                        <h2 className="text-4xl font-bold mb-2 text-white">Your Results</h2>
                        <p className={`text-xl font-bold ${band.class}`}>{band.label}</p>
                        {membershipStatus === 'admin' && <p className="mt-2 text-gray-400 text-sm">Admin View: Right click to print PDF</p>}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {PILLAR_ORDER.map(k => (
                        <div key={k} className="bg-[#1C2B41] p-6 rounded-2xl border border-white/10 text-left shadow-lg">
                            <div className="flex justify-between mb-4">
                                <span className="font-bold text-white">{ASSESSMENT_DATA[k].icon} {ASSESSMENT_DATA[k].name}</span>
                                <span className="text-[#B5D221] font-bold">{scores.pillarScores[k]}%</span>
                            </div>
                            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                                <div className={`h-full bg-gradient-to-r ${getBand(scores.pillarScores[k]).fill}`} style={{ width: `${scores.pillarScores[k]}%` }}></div>
                            </div>
                        </div>
                    ))}
                </div>

                {membershipStatus === 'non-member' && (
                    <div className="bg-[#1C2B41] border border-white/10 p-10 rounded-3xl">
                        <h3 className="text-3xl font-bold mb-4 text-white">Your Next Step</h3>
                        <p className="text-gray-300 mb-8 max-w-xl mx-auto leading-relaxed">Book a free discovery call to discuss your personalised report and how to improve your scores.</p>
                        <a href="https://philipwheelertherapy.com/elevate40plus/" target="_blank" rel="noreferrer" className="inline-block bg-[#B5D221] text-[#0F3E6D] font-bold px-10 py-4 rounded-xl shadow-xl hover:scale-105 transition-transform">Book Discovery Call</a>
                    </div>
                )}
            </div>

            {/* SECRET PRINT VIEW - PDF GENERATION LAYOUT */}
            <div className="hidden print:block text-black text-left p-8">
                <div className="flex justify-between items-center border-b-2 border-gray-200 pb-6 mb-8">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 mb-2">Health Assessment</h1>
                        <p className="text-gray-500 text-sm">Elevate 40+ Comprehensive Report</p>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                        <p className="font-bold text-lg">{adminData?.name || 'Patient Report'}</p>
                        <p>{adminData?.email || ''}</p>
                        <p>{new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                <div className="mb-10 bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Overall Score</h2>
                        <span className="text-3xl font-black text-blue-900">{scores.overall}%</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                        {PILLAR_ORDER.map(k => (
                            <div key={k} className="text-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <div className="text-2xl mb-1">{ASSESSMENT_DATA[k].icon}</div>
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">{ASSESSMENT_DATA[k].name}</div>
                                <div className="text-xl font-bold text-gray-800">{scores.pillarScores[k]}%</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-8">
                    {PILLAR_ORDER.map(k => (
                        <div key={k} className="break-inside-avoid">
                            <h3 className="font-bold text-xl text-gray-800 border-b border-gray-200 pb-2 mb-4 flex items-center gap-2">
                                {ASSESSMENT_DATA[k].icon} {ASSESSMENT_DATA[k].name}
                                <span className="text-sm font-normal text-gray-500 ml-auto">Score: {scores.pillarScores[k]}%</span>
                            </h3>
                            <div className="grid grid-cols-1 gap-2">
                                {ASSESSMENT_DATA[k].questions.map((q, i) => {
                                    const qText = typeof q === 'string' ? q : q.text;
                                    const qLabels = q.labels || DEFAULT_LABELS;

                                    const ansVal = answers[`${k}-${i}`];
                                    const ansText = qLabels[ansVal] || 'Not Answered';
                                    const isLowScore = ansVal <= 1; // Highlight concerning answers
                                    return (
                                        <div key={i} className={`text-sm p-2 rounded ${isLowScore ? 'bg-red-50' : ''}`}>
                                            <div className="font-semibold text-gray-700 mb-1">{qText}</div>
                                            <div className={`text-gray-900 font-medium ${isLowScore ? 'text-red-600' : 'text-green-700'}`}>
                                                Answer: {ansText}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
                    Generated via Elevate 40+ Assessment Tool • philipwheelertherapy.com
                </div>
            </div>
        </div>
    );
};

// --- SECRET ADMIN SCREEN ---
const AdminScreen = ({ onBack }) => {
    const [jsonInput, setJsonInput] = useState('');
    const [adminName, setAdminName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [reportData, setReportData] = useState(null);

    const generateReport = () => {
        try {
            const answers = JSON.parse(jsonInput);
            setReportData(answers);
        } catch (e) { alert("Invalid JSON"); }
    };

    if (reportData) return <ResultsScreen answers={reportData} membershipStatus="admin" adminData={{ name: adminName, email: adminEmail }} />;

    return (
        <div className="max-w-2xl mx-auto py-12 px-4 text-center">
            <h1 className="text-3xl font-bold mb-8">Admin Report Generator</h1>
            <p className="mb-8 text-gray-300">Paste the JSON from the email to generate a clean PDF report.</p>

            <div className="grid grid-cols-2 gap-4 mb-4 text-left">
                <div>
                    <label className="block text-sm text-gray-400 mb-1">Patient Name</label>
                    <input
                        className="w-full bg-[#1C2B41] border border-white/20 rounded-lg p-3 text-white focus:border-[#B5D221] outline-none"
                        placeholder="e.g. John Doe"
                        value={adminName}
                        onChange={e => setAdminName(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-sm text-gray-400 mb-1">Patient Email</label>
                    <input
                        className="w-full bg-[#1C2B41] border border-white/20 rounded-lg p-3 text-white focus:border-[#B5D221] outline-none"
                        placeholder="e.g. john@email.com"
                        value={adminEmail}
                        onChange={e => setAdminEmail(e.target.value)}
                    />
                </div>
            </div>

            <textarea
                className="w-full h-48 bg-[#1C2B41] border border-white/20 rounded-xl p-4 text-white font-mono text-sm mb-6 focus:border-[#B5D221] outline-none"
                placeholder='Paste JSON here... {"a-0":4, "a-1":2 ...}'
                value={jsonInput}
                onChange={e => setJsonInput(e.target.value)}
            ></textarea>

            <div className="flex gap-4 justify-center">
                <button onClick={onBack} className="text-gray-400 hover:text-white">Cancel</button>
                <button onClick={generateReport} className="bg-[#B5D221] text-[#0F3E6D] font-bold px-8 py-3 rounded-lg hover:bg-[#a2b91d]">Generate PDF View</button>
            </div>
        </div>
    );
};

// --- MAIN APP ---
export default function App() {
    const [screen, setScreen] = useState('welcome');
    const [answers, setAnswers] = useState({});
    const [pillarIndex, setPillarIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [userDetails, setUserDetails] = useState(null);
    const [membershipStatus, setMembershipStatus] = useState(null);
    const [isVisible, setIsVisible] = useState(true);
    const [tempSelection, setTempSelection] = useState(null);

    // CHECK FOR ADMIN ROUTE
    useEffect(() => {
        if (window.location.hash === '#admin') setScreen('admin');
    }, []);

    // NEW CENTRALIZED FUNCTION
    const sendToGHL = async (data, contactInfo = userDetails) => {
        try {
            await fetch('/.netlify/functions/updateOrCreateGHLContact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    answers: data,
                    userDetails: contactInfo
                })
            });
        } catch (e) { console.error("GHL Error:", e); }
    };

    const submitToNetlify = (finalAnswers) => {
        const reportData = {
            "form-name": "assessment-results",
            "bot-field": "",
            "contact": `${userDetails.firstName} ${userDetails.lastName || ''} (${userDetails.email})`,
            "report": JSON.stringify(finalAnswers)
        };
        fetch("/", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: encode(reportData)
        }).catch(e => console.error("Netlify Backup Error:", e));
    };

    // UPDATED SUBMIT HANDLER FOR DETAILS (STEP 1)
    const handleDetailsSubmit = (data) => {
        setUserDetails(data);
        // TRIGGER 1: CREATE CONTACT IMMEDIATELY (Empty answers)
        sendToGHL({}, data);
        setScreen('assessment');
    };

    const handleAnswer = (value) => {
        setTempSelection(value);
        setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => {
                const newAnswers = { ...answers, [`${PILLAR_ORDER[pillarIndex]}-${questionIndex}`]: value };
                setAnswers(newAnswers);
                setTempSelection(null);

                const isLastQ = questionIndex === 5;
                const isLastPillar = pillarIndex === 3;

                if (!isLastQ) {
                    setQuestionIndex(q => q + 1);
                    setIsVisible(true);
                } else if (!isLastPillar) {
                    setPillarIndex(p => p + 1);
                    setQuestionIndex(0);
                    confetti({ particleCount: 100, spread: 70, origin: { y: 0.7 }, colors: ['#B5D221', '#ffffff'] });
                    setIsVisible(true);
                } else {
                    // TRIGGER 2: UPDATE CONTACT WITH ANSWERS (Step 2)
                    sendToGHL(newAnswers);
                    submitToNetlify(newAnswers);
                    setScreen('results');
                    confetti({ particleCount: 200, spread: 100, origin: { y: 0.5 }, colors: ['#B5D221', '#ffffff'] });
                }
            }, 300);
        }, 150);
    };

    const handlePrev = () => {
        setIsVisible(false);
        setTimeout(() => {
            if (questionIndex > 0) setQuestionIndex(q => q - 1);
            else if (pillarIndex > 0) {
                setPillarIndex(p => p - 1);
                setQuestionIndex(5);
            }
            setIsVisible(true);
        }, 300);
    };

    const renderScreen = () => {
        switch (screen) {
            case 'admin': return <AdminScreen onBack={() => setScreen('welcome')} />;
            case 'membershipCheck': return <MembershipStatusScreen onBack={() => setScreen('welcome')} onSelect={s => { setMembershipStatus(s); setScreen('userDetails'); }} />;
            case 'userDetails': return <UserDetailsForm type={membershipStatus} onBack={() => setScreen('membershipCheck')} onSubmit={handleDetailsSubmit} />;
            case 'assessment': return <AssessmentScreen pillarKey={PILLAR_ORDER[pillarIndex]} questionIndex={questionIndex} answers={answers} onAnswer={handleAnswer} onPrev={handlePrev} isVisible={isVisible} tempSelection={tempSelection} />;
            case 'results': return <ResultsScreen answers={answers} membershipStatus={membershipStatus} />;
            default: return <WelcomeScreen onStart={() => setScreen('membershipCheck')} />;
        }
    };

    return (
        <div className="min-h-screen bg-[#0F3E6D] text-white overflow-x-hidden print:bg-white print:text-black">
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
            `}</style>
            <Header />
            <main className="container mx-auto pb-20">{renderScreen()}</main>
        </div>
    );
}